import {
  deveSuprimirChromeDoShell,
  ContextoDoShell
} from '@/typescript/servicos/shell/rotasSemMenu'
/** Largura mínima da tela do produto ao lado do chat. */
export const LARGURA_MINIMA_PARA_TELA_LARGA = 720

/** Largura que o shell reserva para o menu lateral. */
export const LARGURA_DO_MENU = 64

/** Largura da coluna do Assistente quando ele participa da grade do shell. */
export const LARGURA_DA_COLUNA_DO_ASSISTENTE = 370

/** Menu e faixa de aplicação reconhecível ao lado do resultado. */
export const LARGURA_DA_FAIXA_DO_RESULTADO = LARGURA_DO_MENU + 80
export const LARGURA_MINIMA_COM_RESULTADO = LARGURA_DA_FAIXA_DO_RESULTADO + LARGURA_DA_COLUNA_DO_ASSISTENTE + LARGURA_MINIMA_PARA_TELA_LARGA

/** Nomes explícitos para consumidores que preferem a linguagem do layout. */
export const LARGURA_DO_MENU_LATERAL = LARGURA_DO_MENU
export const LARGURA_DA_COLUNA_DO_ASSISTENTE_ACOPLADO = LARGURA_DA_COLUNA_DO_ASSISTENTE

export const INTENCAO_PADRAO_DO_ACOPLAMENTO = 'minimizado'

export type IntencaoDoAcoplamento = 'minimizado' | 'recolhido' | 'aberto'
export type FormaDoAcoplamento = 'oculto' | 'minimizado' | 'recolhido' | 'acoplado' | 'sobreposto'

export interface EntradaDaFormaDoAcoplamento {
  intencao: IntencaoDoAcoplamento
  larguraDaJanela: number
  montado: boolean
  resultadoAberto?: boolean
}

/**
 * A forma é derivada durante a renderização. A intenção é a única preferência
 * persistida; largura e montagem são fatos do shell no instante atual.
 *
 * O limite da tela larga mantém espaço para a tela do produto ao lado do chat.
 */
export function formaDoAcoplamento ({
  intencao,
  larguraDaJanela,
  montado,
  resultadoAberto = false
}: EntradaDaFormaDoAcoplamento): FormaDoAcoplamento {
  if (montado !== true) return 'oculto'

  if (intencao === 'minimizado' || intencao === 'recolhido') return intencao
  if (intencao !== 'aberto') return INTENCAO_PADRAO_DO_ACOPLAMENTO

  const larguraValida = typeof larguraDaJanela === 'number' && Number.isFinite(larguraDaJanela)
  const faixa = resultadoAberto ? LARGURA_DA_FAIXA_DO_RESULTADO : LARGURA_DO_MENU
  if (larguraValida && larguraDaJanela - faixa - LARGURA_DA_COLUNA_DO_ASSISTENTE >= LARGURA_MINIMA_PARA_TELA_LARGA) {
    return 'acoplado'
  }

  return 'sobreposto'
}

export type GestoDoAcoplamento =
  | 'acionar'
  | 'expandir'
  | 'enviar'
  | 'fechar'
  | 'escapar'
  | 'navegouComSucesso'
  | 'redimensionar'

function ehIntencao (valor: unknown): valor is IntencaoDoAcoplamento {
  return valor === 'minimizado' || valor === 'recolhido' || valor === 'aberto'
}

/**
 * Transições da intenção. `redimensionar` é deliberadamente um no-op: a forma
 * muda pela largura derivada, mas a conversa não deve fechar nem reiniciar.
 * `navegouComSucesso` e `escapar` só são disparados pelo host quando a forma
 * vigente é sobreposta; como a função recebe a intenção, `aberto` é a única
 * intenção que pode recolher nesses casos — e é por aí que `recolhido` segue
 * vivo, agora que abrir e fechar não passam mais por ele: numa tela estreita a
 * camada sobreposta cobre tudo, então sair dela pela lateral ou por Escape
 * deixa a pílula como rastro da conversa em vez de apagá-la da tela.
 */
export function proximaIntencao (
  intencaoAtual: IntencaoDoAcoplamento,
  gesto: GestoDoAcoplamento
): IntencaoDoAcoplamento {
  const intencao = ehIntencao(intencaoAtual)
    ? intencaoAtual
    : INTENCAO_PADRAO_DO_ACOPLAMENTO

  if (gesto === 'redimensionar') return intencao

  // Clicar no acionador abre a coluna inteira, sem escala na pílula. A parada
  // intermediária foi decisão do dono: quem clica no Assistente quer o
  // Assistente, e receber no lugar uma tira de input no rodapé é uma segunda
  // porta para atravessar antes de chegar onde se queria.
  if (gesto === 'acionar') {
    return intencao === 'minimizado' ? 'aberto' : intencao
  }

  if (gesto === 'expandir' || gesto === 'enviar') {
    return intencao === 'recolhido' ? 'aberto' : intencao
  }

  // Fechar devolve o acionador, não a pílula: o caminho de volta espelha o de
  // ida, em um gesto só.
  if (gesto === 'fechar') {
    if (intencao === 'aberto' || intencao === 'recolhido') return 'minimizado'
    return intencao
  }

  if (gesto === 'escapar' || gesto === 'navegouComSucesso') {
    return intencao === 'aberto' ? 'recolhido' : intencao
  }

  return intencao
}

interface ArmazenamentoDeIntencao {
  getItem: (chave: string) => string | null
  setItem: (chave: string, valor: string) => void
}

/** Chave com namespace do produto e da preferência, sem colidir com o Vuex. */
export const CHAVE_DA_INTENCAO_DO_ACOPLAMENTO = 'pontotel.assistentePontotel.acoplamento.intencao'
export const CHAVE_DA_INTENCAO = CHAVE_DA_INTENCAO_DO_ACOPLAMENTO

function armazenamentoDoNavegador (): ArmazenamentoDeIntencao | null {
  try {
    const armazenamento = globalThis.localStorage
    if (!armazenamento || typeof armazenamento.getItem !== 'function' || typeof armazenamento.setItem !== 'function') return null
    return armazenamento
  } catch {
    return null
  }
}

function intencaoValida (valor: unknown): valor is IntencaoDoAcoplamento {
  return ehIntencao(valor)
}

function intencaoNoValorPersistido (valor: unknown): IntencaoDoAcoplamento | null {
  if (intencaoValida(valor)) return valor
  if (!valor || typeof valor !== 'object') return null
  const objeto = valor as { intencao?: unknown }
  return intencaoValida(objeto.intencao) ? objeto.intencao : null
}

/** Lê a preferência sem deixar JSON inválido derrubar a montagem do shell. */
export function lerIntencaoDoAcoplamento (
  armazenamento: ArmazenamentoDeIntencao | null = armazenamentoDoNavegador()
): IntencaoDoAcoplamento {
  if (!armazenamento) return INTENCAO_PADRAO_DO_ACOPLAMENTO

  try {
    const bruto = armazenamento.getItem(CHAVE_DA_INTENCAO_DO_ACOPLAMENTO)
    if (bruto === null) return INTENCAO_PADRAO_DO_ACOPLAMENTO

    // Aceita tanto o formato simples gravado por versões anteriores quanto o
    // JSON produzido por `persistirIntencaoDoAcoplamento`.
    const direto = intencaoNoValorPersistido(bruto)
    if (direto) return direto

    try {
      const valor = intencaoNoValorPersistido(JSON.parse(bruto))
      return valor ?? INTENCAO_PADRAO_DO_ACOPLAMENTO
    } catch {
      return INTENCAO_PADRAO_DO_ACOPLAMENTO
    }
  } catch {
    return INTENCAO_PADRAO_DO_ACOPLAMENTO
  }
}

/** Persiste apenas valores do contrato; qualquer outro valor vira padrão. */
export function persistirIntencaoDoAcoplamento (
  intencao: IntencaoDoAcoplamento,
  armazenamento: ArmazenamentoDeIntencao | null = armazenamentoDoNavegador()
): void {
  if (!armazenamento) return
  const valor = intencaoValida(intencao) ? intencao : INTENCAO_PADRAO_DO_ACOPLAMENTO
  try {
    armazenamento.setItem(CHAVE_DA_INTENCAO_DO_ACOPLAMENTO, JSON.stringify(valor))
  } catch {
    // Preferência de layout não pode bloquear a conversa quando o navegador
    // está em modo privado ou sem espaço para armazenamento.
  }
}

// Nomes curtos para o host e para consumidores que tratam a preferência como
// uma leitura/escrita convencional. Mantêm uma única implementação e uma só chave.
export const lerIntencaoPersistida = lerIntencaoDoAcoplamento
export const lerIntencao = lerIntencaoDoAcoplamento
export const carregarIntencao = lerIntencaoDoAcoplamento
export const salvarIntencao = persistirIntencaoDoAcoplamento
export const persistirIntencao = persistirIntencaoDoAcoplamento

interface RotaDoContexto {
  path?: unknown
  name?: unknown
  caminhoDaRota?: unknown
  nomeDaRota?: unknown
}

interface JanelaDoContexto {
  location?: {
    hash?: unknown
    search?: unknown
  }
}

export interface ContextoDeMontagemDoAssistente {
  rota?: RotaDoContexto | string | null
  rotaAtual?: RotaDoContexto | string | null
  caminhoDaRota?: unknown
  nomeDaRota?: unknown
  hashDaJanela?: unknown
  janela?: JanelaDoContexto | null
  usuario?: unknown
  user?: unknown
  /** Forma usada pelo store de Flagsmith (`{ enabled: boolean }`). */
  flagDoAssistente?: unknown
  flagAssistente?: unknown
  flagHabilitada?: unknown
  assistenteHabilitado?: unknown
  flag?: unknown
  usuarioAutenticado?: unknown
  contextoDoShell?: Partial<ContextoDoShell>
  shell?: Partial<ContextoDoShell>
}

function rotaDoContexto (rota: ContextoDeMontagemDoAssistente['rota']): RotaDoContexto {
  if (typeof rota === 'string') return { path: rota }
  if (!rota || typeof rota !== 'object') return {}
  return rota
}

function flagLigada (valor: unknown): boolean {
  if (valor === true) return true
  if (!valor || typeof valor !== 'object') return false
  return (valor as { enabled?: unknown }).enabled === true
}

function janelaDoContexto (janela: ContextoDeMontagemDoAssistente['janela']): JanelaDoContexto {
  return janela && typeof janela === 'object' ? janela : {}
}

function chromeSuprimido (contexto: ContextoDeMontagemDoAssistente, rota: RotaDoContexto): boolean {
  const janela = janelaDoContexto(contexto.janela)
  const shell: ContextoDoShell = {
    caminhoDaRota: rota.path ?? rota.caminhoDaRota,
    nomeDaRota: rota.name ?? rota.nomeDaRota,
    hashDaJanela: janela.location?.hash ?? contexto.hashDaJanela
  }

  if (contexto.contextoDoShell) Object.assign(shell, contexto.contextoDoShell)
  if (contexto.shell) Object.assign(shell, contexto.shell)
  return deveSuprimirChromeDoShell(shell)
}

/**
 * Gate puro da montagem no shell. Entrada ausente, usuário ausente, flag
 * desligada, papel diferente de RH/Gestor/Funcionário e chrome suprimido falham fechados.
 */
export function deveMontarOAssistente (
  contexto: ContextoDeMontagemDoAssistente | null | undefined
): boolean {
  if (!contexto || typeof contexto !== 'object') return false

  const rota = rotaDoContexto(
    contexto.rota ?? contexto.rotaAtual ?? {
      path: contexto.caminhoDaRota,
      name: contexto.nomeDaRota
    }
  )
  const usuario = contexto.usuario ?? contexto.user
  const flag = contexto.flagDoAssistente ?? contexto.flagAssistente ?? contexto.flagHabilitada ?? contexto.assistenteHabilitado ?? contexto.flag

  if (contexto.usuarioAutenticado !== undefined && contexto.usuarioAutenticado !== true) return false
  if (usuario === null || usuario === undefined || usuario === false || usuario === '') return false
  if (!flagLigada(flag)) return false
  const kind = (usuario as { roleGroup?: { kind?: unknown } }).roleGroup?.kind
  if (kind !== 'all' && kind !== 'group' && kind !== 'mine') return false
  if (chromeSuprimido(contexto, rota)) return false
  return true
}
